/**
 * permissions.ts — schedule RBAC helpers.
 * Ported from frontend-bk/erp/src/pages/SchedulePage.jsx
 * (canAssignPhoto/Makeup/Retouch logic at lines ~132-143).
 *
 * Logic: admin | director | branch_manager (isFullAccess) can assign all.
 * Managers can assign within their department scope.
 * Reception and sales can assign all roles (they book the session).
 *
 * NOTE: All schedule assignment gates are department-driven (scoped to
 * dept-manager + reception/sale). No clean single BE action mapping during
 * Model A migration — kept on role/dept check. Revisit if BE adds
 * schedule.assign.* or schedule.write actions with dept-aware policy.
 */

import { ROLE } from "@/lib/auth/roles";
import type { Staff } from "@/lib/authv2/store";
import { opsRole } from "@/lib/auth/page-access";

/** Subset of Staff needed for permission checks */
type PermUser = Pick<Staff, "role" | "department" | "jobTitleCode"> | null | undefined;

const isFullAccess = (user: PermUser): boolean =>
  !!user &&
  (user.role === ROLE.Admin ||
    user.role === ROLE.Director ||
    // Trưởng cửa hàng điều hành cơ sở (spec 2026-08-23 §5.7); vai Điều phối đã
    // khoá 23/08/2026 (quyết định 0050) và KHÔNG rơi xuống isDeptLeader.
    // Trưởng ca đi như Trưởng cửa hàng (QĐ 0425) qua `opsRole`.
    opsRole(user) === ROLE.BranchManager);

// QC = leader phòng mình (spec 2026-08-23 §5.6): mọi chỗ "manager + đúng
// phòng" đều nhận qc + đúng phòng.
const isDeptLeader = (user: PermUser): boolean =>
  !!user && (user.role === ROLE.Manager || user.role === ROLE.QC);

const isSales = (user: PermUser): boolean =>
  !!user && (user.department === "sales" || user.department === "sale");

const isReception = (user: PermUser): boolean =>
  !!user && user.department === "reception";

/**
 * Can this user assign a Photographer to an order?
 * Conditions: full-access roles, photo manager, reception, or sales.
 */
export function canAssignPhoto(user: PermUser): boolean {
  if (!user) return false;
  return (
    isFullAccess(user) ||
    (isDeptLeader(user) && !!user.department?.startsWith("photo")) ||
    isReception(user) ||
    isSales(user)
  );
}

/**
 * Can this user assign a Makeup artist to an order?
 * Conditions: full-access roles, makeup manager, reception, or sales.
 */
export function canAssignMakeup(user: PermUser): boolean {
  if (!user) return false;
  return (
    isFullAccess(user) ||
    (isDeptLeader(user) && user.department === "makeup") ||
    isReception(user) ||
    isSales(user)
  );
}

/**
 * Can this user assign a Retoucher to an order?
 * Conditions: full-access roles, retouch manager, reception, or sales.
 */
export function canAssignRetouch(user: PermUser): boolean {
  if (!user) return false;
  // QC chỉ gán khi ở phòng hậu kỳ, kể cả QC ngồi lễ tân (QĐ 0524, khớp máy chủ).
  if (user.role === ROLE.QC) return user.department === "retouch";
  return (
    isFullAccess(user) ||
    (isDeptLeader(user) && user.department === "retouch") ||
    isReception(user) ||
    isSales(user)
  );
}

/**
 * Can this user create a new schedule entry (book a session)?
 * Conditions: full-access roles + manager + sale/reception departments.
 */
export function canCreateScheduleEntry(user: PermUser): boolean {
  if (!user) return false;
  return (
    isFullAccess(user) ||
    isDeptLeader(user) ||
    isSales(user) ||
    isReception(user)
  );
}

/**
 * Can this user create/delete a schedule closure (lịch đóng ca)?
 * Mirrors the BE `schedule.write` action
 * (admin/director/manager/branch_manager/qc — see actions.go):
 * closing the studio is a management decision, narrower than booking a session.
 */
export function canManageClosure(user: PermUser): boolean {
  if (!user) return false;
  return isFullAccess(user) || isDeptLeader(user);
}
